import { forwardRef, useCallback, useId, useRef, useState, type ForwardedRef, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface DropzoneProps {
  /** Called with the chosen file (filtered by `accept`'s primary mime when dropped). */
  onFile: (file: File) => void;
  /** Accept attribute + drop mime filter. Default 'application/pdf'. */
  accept?: string;
  disabled?: boolean;
  title?: ReactNode;
  hint?: ReactNode;
  /** Optional leading icon; a document-upload glyph by default. */
  icon?: ReactNode;
  className?: string;
}

const DefaultIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 16V5m0 0L8 9m4-4 4 4" />
    <path d="M4 15v2a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-2" />
  </svg>
);

/**
 * Dropzone — a drag-and-drop / click file picker. Brand-clean (semantic tokens),
 * keyboard-accessible (it's a real <label> wrapping a hidden <input>).
 */
export const Dropzone = forwardRef(function Dropzone(
  { onFile, accept = 'application/pdf', disabled = false, title, hint, icon, className }: DropzoneProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const [dragging, setDragging] = useState(false);
  const inputId = useId();
  const localRef = useRef<HTMLInputElement | null>(null);

  const mime = accept.split(',')[0]?.trim();
  const accepts = useCallback(
    (f: File) => !mime || mime === '*/*' || (mime.endsWith('/*') ? f.type.startsWith(mime.slice(0, -1)) : f.type === mime),
    [mime],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      if (disabled) return;
      const f = e.dataTransfer.files?.[0];
      if (f && accepts(f)) onFile(f);
    },
    [accepts, disabled, onFile],
  );

  return (
    <label
      htmlFor={inputId}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      data-dragging={dragging || undefined}
      className={cn(
        'group relative flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors',
        dragging ? 'border-primary bg-accent' : 'border-muted-foreground/30 bg-card hover:border-muted-foreground/50 hover:bg-accent',
        disabled && 'pointer-events-none opacity-60',
        className,
      )}
    >
      <span
        className={cn(
          'flex size-11 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition-colors [&_svg]:size-5',
          dragging && 'border-primary text-primary',
          'group-hover:text-foreground',
        )}
      >
        {icon ?? DefaultIcon}
      </span>
      <span className="space-y-1">
        <span className="block text-sm font-medium text-foreground">
          {title ?? (
            <>
              Drop a PDF here, or <span className="text-primary">click to browse</span>
            </>
          )}
        </span>
        {hint ? <span className="block text-xs text-muted-foreground">{hint}</span> : null}
      </span>
      <input
        id={inputId}
        ref={(node) => {
          localRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        type="file"
        accept={accept}
        disabled={disabled}
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
    </label>
  );
});
